import { LatLng } from "use-places-autocomplete";
import { OrderTypesKeysEnum, ReceiptItemStateEnum } from "../../enums";
import {
  Address,
  AddressPlus,
  DeliveryConfigZone,
  DeliveryReceiptStatusEnum,
  Franchise,
  FranchiseDeliveryConfig,
  PaidReceipt,
} from "../../types";
import { pointInPolygon } from "../maths/maths";

export const checkAddressAvailabliltyInFranchisesV2 = (
  address: Address,
  franchises: Franchise[],
  franchiseSlug: string | string[] | undefined,
  currentDeliveryTypeId: number | undefined
) => {
  // // check if address is within current franchise
  const currentLocation = {
    lat: address.lat,
    lng: address.lng,
  };

  let isInCurrentFranchise = false;
  const availableDeliveryFranchises = [];
  let zoneConfig = null;
  if (currentDeliveryTypeId) {
    for (let index = 0; index < franchises.length; index++) {
      const franchiseCur = franchises[index];
      if (franchiseCur.franchiseConfig.deliveryV2) {
        // Check which franchise has delivery V2
        const franchiseDeliveryConfigCurr: FranchiseDeliveryConfig[] =
          franchiseCur.franchiseConfig.deliveryV2;

        const franchiseCurDeliveryConfig = franchiseDeliveryConfigCurr.find(
          (config) => config.deliveryType.id === currentDeliveryTypeId
        );
        // Check which franchise has same delivery type
        if (franchiseCurDeliveryConfig) {
          const inZone = inFranchiseDeliveryZoneV2(
            currentLocation,
            franchiseCurDeliveryConfig.deliveryConfigZones
          );
          if (inZone) {
            if (
              franchiseCur.slug === franchiseSlug ||
              (!franchiseCur.slug && !franchiseSlug)
            ) {
              isInCurrentFranchise = true;
              zoneConfig = inZone;
            } else {
              availableDeliveryFranchises.push(franchiseCur as Franchise);
            }
          }
        }
      }
    }
  }

  return {
    ...address,
    isInCurrentFranchise,
    availableDeliveryFranchises,
    zoneConfig,
  };
};

/**
 * The delivery config to commit when the guest picks a delivery TYPE.
 *
 * `currentDeliveryConfigId` is a config ROW id, and every branch has its own
 * row per delivery type. The order-type drawer, the address drawer and the V3
 * map tab used to commit the first row of that type across every active
 * branch -- i.e. whichever branch is listed first, not the branch being
 * ordered from. With "arkan" listed before "hub", a hub guest who picked
 * Delivery was given arkan's row; useSearchBox.validateDeliveryZone then
 * tested their address against arkan's polygons, its "find another branch"
 * fallback skips the selected branch, and an address squarely inside hub was
 * reported as outside delivery.
 *
 * So: the selected branch's own row when it offers the type. The cross-branch
 * fallback stays for the two cases that need it -- no branch selected yet
 * (the hub model resolves the branch from the address afterwards), and a
 * selected branch that does not offer the type at all.
 */
export const findDeliveryConfigForBranch = (
  selectedFranchise: Franchise | null | undefined,
  franchises: Franchise[],
  matches: (config: FranchiseDeliveryConfig) => boolean
): FranchiseDeliveryConfig | undefined =>
  selectedFranchise?.franchiseConfig?.deliveryV2?.find(matches) ??
  franchises
    .flatMap((franchise) => franchise.franchiseConfig?.deliveryV2 ?? [])
    .find(matches);

export const sortAddress = (a: AddressPlus, b: AddressPlus) => {
  if (a.isInCurrentFranchise && !b.isInCurrentFranchise) {
    return -2;
  } else if (
    !a.isInCurrentFranchise &&
    !b.isInCurrentFranchise &&
    a.availableDeliveryFranchises.length > 0 &&
    b.availableDeliveryFranchises.length === 0
  ) {
    return -1;
  } else {
    return 1;
  }
};

const hasInvalidCoordinate = (coordinate: unknown) =>
  coordinate == null ||
  coordinate === "" ||
  !Number.isFinite(Number(coordinate));

/**
 * Mirrors the backend's `checkInDeliveryV2Zone` guard (prepareit-backend
 * `utils/help.ts`): a zone whose polygon carries a null/non-finite coordinate is
 * treated as unmatched and skipped so the loop can still match a valid zone.
 *
 * Without this the two sides can pick *different* zones for the same address —
 * the backend skips a corrupted zone while the frontend feeds its null latitude
 * into pointInPolygon — and then quote different delivery fees for one order.
 * Corrupted zones are live data, not hypothetical (eg has zones stored as
 * `[null, lng]`).
 */
export const inFranchiseDeliveryZoneV2 = (
  center: LatLng,
  deliveryZones: DeliveryConfigZone[]
) => {
  let inZone = null;
  for (let index = 0; index < deliveryZones.length; index++) {
    const deliveryZone = deliveryZones[index].deliveryZone.polygon;
    const hasBadPoint = deliveryZone.some(
      (latlng: number[]) =>
        !Array.isArray(latlng) ||
        hasInvalidCoordinate(latlng[0]) ||
        hasInvalidCoordinate(latlng[1])
    );

    if (hasBadPoint) {
      continue;
    }

    const deliveryZoneInLatLng = deliveryZone.map((latlng: number[]) => {
      return { lat: latlng[0], lng: latlng[1] };
    });

    if (pointInPolygon(center, deliveryZoneInLatLng)) {
      inZone = {
        minEstimatedTime: deliveryZones[index].minEstimatedTime,
        maxEstimatedTime: deliveryZones[index].maxEstimatedTime,
        deliveryFees: deliveryZones[index].deliveryFees,
        deliveryZoneId: deliveryZones[index].deliveryZoneId,
      };
      break;
    }
  }

  return inZone;
};

export const isReceiptDeliveryPaidAndCompleted = (receipt: PaidReceipt) => {
  return !!(
    receipt.paid_at &&
    receipt.state === ReceiptItemStateEnum.COMPLETED &&
    receipt.orderType === OrderTypesKeysEnum.DELIVERYV2 &&
    receipt.deliveryReceipt &&
    receipt.deliveryReceipt.maxEstimatedTime
  );
};

export const isDeliveryReceiptOnItsWayAndDelivered = (receipt: PaidReceipt) => {
  return !!(
    receipt.paid_at &&
    receipt.orderType === OrderTypesKeysEnum.DELIVERYV2 &&
    receipt.deliveryReceipt &&
    receipt.deliveryReceipt.deliveryStatus ===
      DeliveryReceiptStatusEnum.DELIVERED &&
    !isDeliveryReceiptTimeDone(receipt)
  );
};

export const isDeliveryReceiptOnItsWay = (receipt: PaidReceipt) => {
  return !!(
    isReceiptDeliveryPaidAndCompleted(receipt) &&
    !isDeliveryReceiptTimeDone(receipt)
  );
};

export const isDeliveryReceiptTimeDone = (receipt: PaidReceipt) => {
  const completedAt = new Date(receipt.completedAt);
  const currentDate = new Date();
  const deliveryWindow = new Date(
    completedAt.getTime() +
      (receipt.deliveryReceipt?.maxEstimatedTime! + 15) * 60000
  );

  return !!(
    isReceiptDeliveryPaidAndCompleted(receipt) &&
    deliveryWindow.getTime() <= currentDate.getTime()
  );
};
